import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

interface ApiResponse<T> {
  code: number;
  status: string;
  message: string;
  data: T;
  error?: string;
}

export interface BatchDeclineRequest {
  proposalIds: string[];
  declineReason: string;
  declineOtherReason: string;
  declineComments: string;
}

export interface BatchDeclineProductResult {
  proposalId: string;
  siteId: string;
  outcome: 'DECLINED' | 'SKIPPED' | 'FAILED';
  status: string | null;
}

export interface BatchDeclineResult {
  total: number;
  declined: number;
  skipped: number;
  failed: number;
  products: BatchDeclineProductResult[];
}

@Injectable({
  providedIn: 'root',
})
export class ProposalService {
  constructor(private http: HttpClient, private toastr: ToastrService) {}

  // Returns the PUT /proposal request (with the shared FAILURE toast) WITHOUT
  // subscribing, so a caller that must gate on the result — e.g. only clearing
  // a free-text reply once it actually persists — can subscribe and react to
  // success/failure itself.
  public saveProposalRequest(proposal: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http
      .put<any>(environment.apiUrl + '/proposal', JSON.stringify(proposal), {
        headers,
        responseType: 'json',
      })
      .pipe(
        tap((response) => {
          let apiResponse: any = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        })
      );
  }

  // Fire-and-forget save — the existing bid-note / turn-time / bid-amount
  // callers rely on this signature.
  public saveProposal(proposal: any): void {
    this.saveProposalRequest(proposal).subscribe();
  }

  public submitBid(projectDetail: any, userId: any): any {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.put<any>(
      environment.apiUrl + `/proposal/submit-bid/${userId}`,
      JSON.stringify(projectDetail),
      {
        headers,
        responseType: 'json',
      }
    );
  }

  public acceptOffer(projectDetail: any, userId: any): any {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.post<any>(
      environment.apiUrl + `/proposal/accept-offer/${userId}`,
      JSON.stringify(projectDetail),
      {
        headers,
        responseType: 'json',
      }
    );
  }

  // PA-facing counter-offer (POST /proposal/counter-offer/{userId}). A narrow
  // body — proposalId + the three counter values — mirroring
  // CounterOfferRequest on connect-service; unlike acceptOffer/editBid this
  // never carries the whole project/proposal document. Legal only from
  // OFFERED or RE_OFFERED, enforced server-side against the PERSISTED status
  // — COUNTERED is deliberately NOT counterable: one counter per offer, and
  // the next move on a standing counter is staffing's;
  // {userId} is retained for route shape but the acting user is resolved from
  // the authenticated principal.
  public counterOffer(
    counterOfferRequest: {
      proposalId: any;
      counterFee: number;
      counterCompleteBy?: string | null;
      counterNote?: string | null;
    },
    userId: any
  ): any {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.post<any>(
      environment.apiUrl + `/proposal/counter-offer/${userId}`,
      JSON.stringify(counterOfferRequest),
      {
        headers,
        responseType: 'json',
      }
    );
  }

  public editBid(proposal: any): any {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http
      .put<any>(
        environment.apiUrl + '/proposal/edit-bid',
        JSON.stringify(proposal),
        {
          headers,
          responseType: 'json',
        }
      )
      .pipe(
        tap((response) => {
          const apiResponse: any = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        })
      )
      .subscribe();
  }

  public declineInvitation(proposal: any): any {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.put<any>(
      environment.apiUrl + '/proposal/decline-invitation',
      JSON.stringify(proposal),
      {
        headers,
        responseType: 'json',
      }
    );
  }

  public declineInvitationBatch(
    request: BatchDeclineRequest
  ): Observable<ApiResponse<BatchDeclineResult>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this.http.put<ApiResponse<BatchDeclineResult>>(
      environment.apiUrl + '/proposal/decline-invitation/batch',
      JSON.stringify({
        proposalIds: request.proposalIds,
        declineReason: request.declineReason,
        declineOtherReason: request.declineOtherReason,
        declineComments: request.declineComments,
      }),
      { headers, responseType: 'json' }
    );
  }

  public submitReadyForReview(proposalId: any): any {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.put<any>(
      `${environment.apiUrl}/proposal/submit-review/${proposalId}`,
      {},
      { headers, responseType: 'json' }
    );
  }

  public submitRevision(proposalId: any): any {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.put<any>(
      `${environment.apiUrl}/proposal/submit-review-revision/${proposalId}`,
      {},
      { headers, responseType: 'json' }
    );
  }

  // Neither site-visit route carries a user id: connect-service resolves the
  // acting user from the authenticated principal and verifies that user owns the
  // persisted proposal before writing anything, so a caller cannot nominate whose
  // proposal it is acting on.
  public submitSiteVisit(proposal: any): any {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this.http.post<any>(
      `${environment.apiUrl}/proposal/schedule-site-visit`,
      JSON.stringify(proposal),
      {
        headers,
        responseType: 'json',
      }
    );
  }

  // Whole-site (batch) site-visit submit — the consolidated scheduler proposes
  // ONE set of windows for the physical site and connect-service fans the
  // identical windows out to every product (proposal) that requires a visit.
  // `batchRequest` is `{ siteVisit1, siteVisit2, siteVisit3, proposalIds }`; the
  // response's `data` is a `BatchSiteVisitResult`
  // (`{ total, submitted, failed, products }`) so the caller can surface
  // per-product success/failure. `proposalIds` is deduplicated server-side and
  // every id must belong to the authenticated user — one unowned or unusable id
  // rejects the whole batch, so `total` counts DISTINCT ids. The single
  // `submitSiteVisit` above is kept for the two per-order site-visit screens
  // (the site-visit dialog and the site-visit detail screen).
  public submitSiteVisitBatch(batchRequest: any): any {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this.http.post<any>(
      `${environment.apiUrl}/proposal/schedule-site-visit/batch`,
      JSON.stringify(batchRequest),
      {
        headers,
        responseType: 'json',
      }
    );
  }

  public saveExpenses(proposal: any): void {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    this.http
      .put<any>(
        environment.apiUrl + `/proposal/expenses/${proposal.userId}`,
        JSON.stringify(proposal),
        {
          headers,
          responseType: 'json',
        }
      )
      .pipe(
        tap((response) => {
          let apiResponse: any = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        })
      )
      .subscribe();
  }
}
